package com.kanati.shared.commerce

import kotlinx.serialization.Serializable

/** Fulfillment state, independent of payment status. COMPLETED and CANCELED are terminal. */
@Serializable
enum class ConcessionOrderStatus {
    RECEIVED,
    IN_PREP,
    READY,
    COMPLETED,
    CANCELED,
}

/** Backend-established provenance. CONSUMER_MOBILE does not imply checkout is enabled. */
@Serializable
enum class ConcessionOrderSource {
    POS_COUNTER,
    CONSUMER_MOBILE,
}

/**
 * Backend-authoritative fulfillment snapshot for one completed concessions sale.
 * Line items reuse the immutable sale snapshots; their subtotals exclude sale-level tax/discounts.
 * Timestamps are backend UTC instant strings. Pickup labels are not access credentials.
 * See docs/cop/contracts/concession_order_fulfillment.md for authority and lifecycle rules.
 */
@Serializable
data class ConcessionOrderDto(
    val id: String,
    val districtId: String,
    val schoolId: String,
    val saleId: String,
    val source: ConcessionOrderSource,
    val status: ConcessionOrderStatus,
    val displayNumber: String,
    val lineItems: List<SaleLineItemResponse>,
    val createdAt: String,
    val updatedAt: String,
    val pickupName: String? = null,
    val readyAt: String? = null,
    val completedAt: String? = null,
    val cancellationReason: String? = null,
)

/**
 * Order id is supplied in request context. Backend validates scope, operator access,
 * expectedStatus and allowed transitions atomically; constructing this DTO changes no state.
 * Cancellation requires a nonblank reason; other target statuses require no reason.
 * Matching current target/reason is a no-op; delayed retries never move an order backward.
 */
@Serializable
data class UpdateConcessionOrderStatusRequest(
    val districtId: String,
    val schoolId: String,
    val expectedStatus: ConcessionOrderStatus,
    val status: ConcessionOrderStatus,
    val cancellationReason: String? = null,
)
